import React from 'react';
import { DollarSign, Heart, ShoppingCart, Users, Award } from 'lucide-react';
import { Translate } from 'react-redux-i18n';
import { EntityWithComparison, PageType, MetricKey } from '../types';
import {
  getMetricValueForEntity,
  shouldShowSalesRow,
  shouldShowFundraisingRow,
  shouldShowPlaceHubRow
} from '../utils';
import CurrencyHelper from '../../../../helpers/currencyHelper';
import { MetricCard } from './MetricCard';
import { MetricsGrid } from './MetricsGrid';
import SystemOnlyBadge from '../../../../components/common/SystemOnlyBadge';

export interface EntityCardProps {
  entity: EntityWithComparison;
  includeOffline: boolean;
  showAllMetrics: boolean;
  isSystemAdmin: boolean;
  isAnimating: boolean;
  selectedMetric: MetricKey | null;
}

export const EntityCard: React.FC<EntityCardProps> = ({
  entity,
  includeOffline,
  showAllMetrics,
  isSystemAdmin,
  isAnimating,
  selectedMetric
}) => {
  const performanceUrl =
    entity.linkedEntityType === PageType.CAMPAIGN
      ? `/campaigns/performance/${entity.id}`
      : `/events/performance/${entity.id}`;

  // Check what rows to show based on entity data
  const hasSales = shouldShowSalesRow(entity);
  const hasFundraising = shouldShowFundraisingRow(entity);
  const hasPlaceHub = shouldShowPlaceHubRow(entity);

  // Calculate fee percentage for sysadmin display
  const raisedAmount = getMetricValueForEntity(entity, 'raisedAmount', includeOffline);
  const feeCollected = entity.feeCollected || 0;
  const feePercentage = raisedAmount > 0 ? (feeCollected / raisedAmount) * 100 : 0;

  // Format fee value with percentage
  const formatFeeValue = (value: number) => (
    <>
      {CurrencyHelper.formatCurrency(value, entity.currency, { alwaysShowDecimals: true })}
      <span style={{ marginLeft: '0.5rem', opacity: 0.7, fontSize: '0.9em' }}>
        ({feePercentage.toFixed(1)}%)
      </span>
    </>
  );

  // Helper to get selected class for a metric
  const getMetricClass = (metric: MetricKey) => {
    return selectedMetric === metric ? 'franchise-metric-selected' : '';
  };

  // Row 1 - Always visible (3 metrics)
  const row1Metrics = (
    <>
      <MetricCard
        label={<Translate value="franchise.hub.metrics.raised-amount" />}
        value={raisedAmount}
        isCurrency
        currency={entity.currency}
        yoyChange={entity.yoyChanges?.raisedAmount}
        isAnimating={isAnimating}
        icon={<DollarSign />}
        colorClass={getMetricClass('raisedAmount')}
      />

      <MetricCard
        label={
          includeOffline ? (
            <Translate value="franchise.hub.metrics.donation-count" />
          ) : (
            <Translate value="franchise.hub.metrics.donation-count-online" />
          )
        }
        value={getMetricValueForEntity(entity, 'donationCount', includeOffline)}
        yoyChange={entity.yoyChanges?.donationCount}
        isAnimating={isAnimating}
        icon={<Heart />}
        colorClass={getMetricClass('donationCount')}
      />

      {isSystemAdmin ? (
        <MetricCard
          label={
            <>
              <Translate value="franchise.hub.metrics.fee-collected" />
              <SystemOnlyBadge />
            </>
          }
          value={feeCollected}
          formatValue={formatFeeValue}
          yoyChange={entity.yoyChanges?.feeCollected}
          isAnimating={isAnimating}
          colorClass={getMetricClass('feeCollected')}
        />
      ) : (
        <MetricCard
          label={
            includeOffline ? (
              <Translate value="franchise.hub.metrics.avg-donation" />
            ) : (
              <Translate value="franchise.hub.metrics.avg-donation-online" />
            )
          }
          value={getMetricValueForEntity(entity, 'donationAverageAmount', includeOffline)}
          isCurrency
          currency={entity.currency}
          yoyChange={entity.yoyChanges?.donationAverageAmount}
          isAnimating={isAnimating}
          colorClass={getMetricClass('donationAverageAmount')}
        />
      )}
    </>
  );

  // Row 2 - Sales (show if hasSales)
  const row2Metrics = hasSales && showAllMetrics && (
    <>
      <MetricCard
        label={
          includeOffline ? (
            <Translate value="franchise.hub.metrics.sale-amount" />
          ) : (
            <Translate value="franchise.hub.metrics.sale-amount-online" />
          )
        }
        value={getMetricValueForEntity(entity, 'saleRaisedAmount', includeOffline)}
        isCurrency
        currency={entity.currency}
        yoyChange={entity.yoyChanges?.saleRaisedAmount}
        isAnimating={isAnimating}
        icon={<ShoppingCart />}
        colorClass={getMetricClass('saleRaisedAmount')}
      />

      <MetricCard
        label={
          includeOffline ? (
            <Translate value="franchise.hub.metrics.donation-amount" />
          ) : (
            <Translate value="franchise.hub.metrics.donation-amount-online" />
          )
        }
        value={getMetricValueForEntity(entity, 'donationRaisedAmount', includeOffline)}
        isCurrency
        currency={entity.currency}
        yoyChange={entity.yoyChanges?.donationRaisedAmount}
        isAnimating={isAnimating}
        colorClass={getMetricClass('donationRaisedAmount')}
      />

      <MetricCard
        label={<Translate value="franchise.hub.metrics.ticket-count" />}
        value={entity.saleDetailCount || 0}
        yoyChange={entity.yoyChanges?.saleDetailCount}
        isAnimating={isAnimating}
        colorClass={getMetricClass('saleDetailCount')}
      />
    </>
  );

  // Row 3 - Fundraising (show if hasFundraising)
  const row3Metrics = hasFundraising && showAllMetrics && (
    <>
      <MetricCard
        label={<Translate value="franchise.hub.metrics.page-count" />}
        value={getMetricValueForEntity(entity, 'pageCount', includeOffline)}
        yoyChange={entity.yoyChanges?.pageCount}
        isAnimating={isAnimating}
        icon={<Users />}
        colorClass={getMetricClass('pageCount')}
      />

      <MetricCard
        label={<Translate value="franchise.hub.metrics.pages-with-donations" />}
        value={getMetricValueForEntity(entity, 'pagesWithDonations', includeOffline)}
        yoyChange={entity.yoyChanges?.pagesWithDonations}
        isAnimating={isAnimating}
        colorClass={getMetricClass('pagesWithDonations')}
      />

      <MetricCard
        label={<Translate value="franchise.hub.metrics.fundraiser-avg-raised" />}
        value={entity.fundraiserAverageRaisedAmount || 0}
        isCurrency
        currency={entity.currency}
        yoyChange={entity.yoyChanges?.fundraiserAverageRaisedAmount}
        isAnimating={isAnimating}
        colorClass={getMetricClass('fundraiserAverageRaisedAmount')}
      />
    </>
  );

  // Row 4 - PlaceHub (show if hasPlaceHub)
  const row4Metrics = hasPlaceHub && showAllMetrics && (
    <>
      <MetricCard
        label={<Translate value="franchise.hub.metrics.ambassador-total" />}
        value={entity.ambassadorTotalCount || 0}
        yoyChange={entity.yoyChanges?.ambassadorTotalCount}
        isAnimating={isAnimating}
        icon={<Award />}
        colorClass={getMetricClass('ambassadorTotalCount')}
      />

      <MetricCard
        label={<Translate value="franchise.hub.metrics.ambassador-pages" />}
        value={entity.ambassadorPageCount || 0}
        yoyChange={entity.yoyChanges?.ambassadorPageCount}
        isAnimating={isAnimating}
        colorClass={getMetricClass('ambassadorPageCount')}
      />

      <MetricCard
        label={
          includeOffline ? (
            <Translate value="franchise.hub.metrics.ambassador-raised" />
          ) : (
            <Translate value="franchise.hub.metrics.ambassador-raised-online" />
          )
        }
        value={getMetricValueForEntity(entity, 'ambassadorFundraisedAmount', includeOffline)}
        isCurrency
        currency={entity.currency}
        yoyChange={entity.yoyChanges?.ambassadorFundraisedAmount}
        isAnimating={isAnimating}
        colorClass={getMetricClass('ambassadorFundraisedAmount')}
      />
    </>
  );

  return (
    <div className="franchise-entity-card">
      <div className="franchise-entity-card-header">
        <h3 className="franchise-entity-card-title">
          <a href={performanceUrl} className="franchise-entity-card-link">
            {entity.name}
          </a>
        </h3>
        {entity.year && (
          <span className="franchise-entity-card-year">{entity.year}</span>
        )}
      </div>

      {/* Row 1 - Always visible */}
      <MetricsGrid columns={3}>
        {row1Metrics}
      </MetricsGrid>

      {/* Rows 2-4 - Show when showAllMetrics is true */}
      {showAllMetrics && (
        <>
          {row2Metrics && (
            <MetricsGrid columns={3}>
              {row2Metrics}
            </MetricsGrid>
          )}

          {row3Metrics && (
            <MetricsGrid columns={3}>
              {row3Metrics}
            </MetricsGrid>
          )}

          {row4Metrics && (
            <MetricsGrid columns={3}>
              {row4Metrics}
            </MetricsGrid>
          )}
        </>
      )}
    </div>
  );
};
